/**
 * SaveToDriveDialog — the Save-to-drive DIALOG (#639, Task 6.2), the
 * user-triggered twin of the `move_to_drive` LLM tool (renamed from
 * `save_to_drive` — #662 Task 5; Task 6.1). A
 * right-click on an attachment chip / snapshot opens this: pick a
 * destination folder (default: the tenant drive root, with an override via
 * a drive folder picker), confirm, and the attachment is copied into the
 * drive AS THE HUMAN — the endpoint
 * (`POST .../attachments/:handle/save-to-drive`) runs as the REQUEST's own
 * authenticated user, never the agent, so the human's own permission on the
 * chosen folder is what's checked (a view-only user picking a folder they
 * can't write to gets the SAME 403 back as a dialog error, not a silent
 * failure).
 *
 * invariant #2 (design doc §2): drive entry is a DELIBERATE privacy
 * downgrade — "the single point where all normal drive-write gates apply."
 * This dialog surfaces that cost up front, every time, rather than
 * absorbing it into a one-click action.
 *
 * ── Why its own folder picker instead of `@papan/workspace`'s `FolderPickerDialog` ──
 *
 * `@papan/workspace` depends on `@papan/agent` (its package.json), so an
 * `@papan/agent` UI component importing `@papan/workspace/ui` back would be
 * a circular PACKAGE dependency. `@papan/files/ui/picker` already exports
 * the pieces a drive folder-drilldown needs (`PickerNavigator`,
 * `getRootFolderId`, `isRootFolderId`) precisely so a consumer can build one
 * without depending on `@papan/workspace` — `features/files/ui/picker-utils.ts`'s
 * own header comment states the same reasoning for why ITS copy of
 * `getRootFolderId`/`isRootFolderId` isn't imported from workspace either.
 * `features/agent` already depends on `@papan/files`, so this follows the
 * same precedent rather than inventing a new one.
 */
import { useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Folder, Loader2, AlertTriangle, X, ChevronLeft } from 'lucide-react'
import { Modal, useTheme, getThemeClass, useConflict } from '@papan/core-ui/ui'
import { getActiveTenantId, isSessionEndedError, pinnedBatch, isPinnedBatchIdentityLost, type PinnedBatch } from '@papan/api-client'
import { damagedMessageFrom } from '@papan/types'
import { useBrand } from '@papan/brand/react'
import { PickerNavigator, getRootFolderId, type PickerCrumb, type PickerRoot } from '@papan/files/ui/picker'
import { requestSaveSessionAttachmentToDrive, requestMoveInlineAttachmentToDrive, uploadSessionAttachment } from './sessionAttachmentsApi.js'

export interface SaveToDriveDialogProps {
  filename: string
  /** A CAS-backed attachment's chat handle (upload/paste/drop/snapshot).
   *  Provide this OR `url`. */
  handle?: string
  /** An INLINE attachment's `data:` URL (a small image / text file whose bytes
   *  live in the message part, with no CAS handle). Its bytes are materialized
   *  into the session store first, then saved through the SAME gate stack the
   *  handle path uses — so inline attachments are saveable to drive too. */
  url?: string
  /** The inline attachment's mime, used when materializing its bytes. */
  mime?: string
  sessionId: string
  workspaceId: string
  /** The source chip's message + message-part ids (#662 Task 7) — when
   *  `messagePartId` is supplied, the endpoint re-points that part at the new
   *  drive file (turning the copy into a move); the caller uses these to
   *  re-point the chip locally too, via `onSaved`'s `fileId`. */
  messageId?: string
  messagePartId?: string
  onClose: () => void
  /** `path` is the server's `/papan/...` VFS path for the saved file (#662) —
   *  what the caller stores on a moved chip so it re-opens the live drive
   *  file. `fileId` is the drive file's own id. */
  onSaved: (result: { fileName: string; path: string; fileId: string }) => void
}

/** Maps the endpoint's `code` (features/agent/api/routes/attachments.ts) to
 *  a sentence written for the person at this dialog — mirrors the copy
 *  conventions `apps/api/src/middleware/error.ts` uses for the same
 *  underlying refusals elsewhere in the app. */
function describeSaveError(err: { message?: string; code?: string }): string {
  // #1397: a damaged stored copy — the server's own sentence
  const damaged = damagedMessageFrom(err)
  if (damaged) return damaged
  switch (err.code) {
    case 'TENANT_WRITE_FROZEN': return 'Uploads are paused for this workspace.'
    case 'PERMISSION_DENIED': return "You can't write to that folder."
    case 'STORAGE_QUOTA_EXCEEDED': return 'Storage is full.'
    case 'IDENTITY_ONLY': return 'This workspace cannot receive new drive content.'
    case 'MODIFICATION_SAFEGUARD': return "That folder is inside an app — you need the app's own access to move it there."
    case 'CROSS_TENANT_UNSUPPORTED': return 'Moving into a cross-tenant shared location is not supported yet.'
    case 'ATTACHMENT_NOT_FOUND': return 'This attachment is no longer available.'
    default: return err.message || 'Could not move to drive.'
  }
}

/** A compact, single-column destination picker — same drilldown shape as
 *  `@papan/workspace`'s `FolderPickerDialog` (issue #21), trimmed to just
 *  what this dialog needs. See the module header for why this doesn't just
 *  import that component. */
function DestinationPicker({
  tenantId, onSelect, onClose,
}: {
  tenantId: string
  onSelect: (folderId: string, path: string) => void
  onClose: () => void
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const rootId = useMemo(() => getRootFolderId(tenantId), [tenantId])

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedPath, setSelectedPath] = useState<string | null>(null)

  // `folderId` is nullable since main widened PickerNavigator's onNavigate (a
  // drive-root landing reports null). The Choose button already guards on
  // `!selectedId`, so a null selection simply stays un-choosable.
  const handleNavigate = useCallback((folderId: string | null, breadcrumbs: PickerCrumb[]) => {
    setSelectedId(folderId)
    setSelectedPath('/' + breadcrumbs.map(c => c.name).join('/'))
  }, [])

  // The drive-root breadcrumb label is brand-derived — never the literal
  // product name — since it renders in the picker footer, the dialog's
  // destination path, and the chip's "Saved to …" reflection (whitelabel).
  const { productName } = useBrand()
  const roots = useMemo<PickerRoot[]>(() => [{ id: rootId, label: productName }], [rootId, productName])

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} ws-glass-modal ws-anim-modal-in relative w-full max-w-[600px] h-[360px] max-h-[90vh] flex flex-col`}>
        <div className="flex items-center justify-between px-4 py-3 border-b themed-border">
          <div className="flex items-center gap-2">
            <ChevronLeft size={14} className="themed-text-muted" />
            <h3 className="text-sm font-medium themed-text">Choose a folder</h3>
          </div>
          <button onClick={onClose} className="themed-text-muted hover:themed-text">
            <X size={16} />
          </button>
        </div>

        <PickerNavigator
          roots={roots}
          initialFolderId={rootId}
          mode="folder"
          selectedId={selectedId}
          onSelect={() => { /* folder mode — files are hidden, selection comes from onNavigate */ }}
          onNavigate={handleNavigate}
          splitView
          autoFocusSearch
          className="flex-1 min-h-0"
        />

        <div className="flex items-center justify-between px-4 py-3 border-t themed-border">
          <div className="text-xs themed-text-muted truncate max-w-[380px]">
            {selectedPath || 'Select a folder'}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm themed-text-secondary hover:themed-text rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => { if (selectedId && selectedPath) onSelect(selectedId, selectedPath) }}
              disabled={!selectedId}
              className="px-3 py-1.5 text-sm themed-btn-primary rounded transition-colors disabled:opacity-50"
            >
              Select
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/**
 * The idempotency key for materializing an inline attachment's bytes before a
 * save. The server dedupes uploads on (session, key) and hands back the FIRST
 * row for a repeated key — so the key must name the CONTENT. It used to be
 * `filename:size`: two different files with the same name and size in one chat
 * (two `notes.txt` of 4 KB) got the first one's row, and saving the second wrote
 * the FIRST file's bytes to the drive. A SHA-256 of the bytes keeps the dedup a
 * re-save of the same file wants. Where `crypto.subtle` is missing (a non-secure
 * origin), a random key: never wrong, it only skips the dedup.
 */
export async function saveToDriveIdempotencyKey(filename: string, blob: Blob): Promise<string> {
  const subtle = globalThis.crypto?.subtle
  if (!subtle) return `save-to-drive:${filename}:${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`}`
  const digest = new Uint8Array(await subtle.digest('SHA-256', await blob.arrayBuffer()))
  const hex = Array.from(digest, b => b.toString(16).padStart(2, '0')).join('')
  return `save-to-drive:${filename}:${hex}`
}

export function SaveToDriveDialog({
  filename, handle, url, mime, sessionId, workspaceId, messageId, messagePartId, onClose, onSaved,
}: SaveToDriveDialogProps) {
  const tenantId = getActiveTenantId()
  // The drive's OWN shared conflict modal (#662) — the SAME resolver the drive
  // copy/move/upload paths use, so a same-name collision here offers the full
  // option set (Replace / Keep both / Skip) instead of a bespoke
  // Continue/Cancel notice. Requires a `ConflictProvider` ancestor, which sits
  // at the SPA root (apps/web/src/App.tsx) above every chat surface.
  const resolveConflict = useConflict()

  // null → the tenant drive root, the sensible default. `destDisplay` is
  // "My Drive" for the root and a root-relative breadcrumb (e.g.
  // "/Reports/Q3") once the picker below sets a specific folder.
  const [destParentId, setDestParentId] = useState<string | null>(null)
  const [destDisplay, setDestDisplay] = useState('My Drive')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The materialized handle for an inline (`url`-only, no `handle` prop)
  // attachment, cached after the FIRST materialize so a conflict-resolution
  // re-call reuses it instead of re-uploading the bytes a second time.
  const [materializedHandle, setMaterializedHandle] = useState<string | undefined>(undefined)
  // True for the span of `await resolveConflict(...)` below. Both this
  // dialog's own `<Modal>` and the drive's shared `ConflictModal` are
  // full-screen `z-50` overlays; this one is portaled to `document.body`
  // AFTER the `ConflictProvider`'s portal (which sits inside `#root`), so it
  // stacks on top and hides the conflict UI the user needs to act on. This
  // flag suppresses this dialog's own modal markup while the conflict
  // resolver is up, WITHOUT unmounting the component — the in-flight
  // `handleSave` async (and the cached `materializedHandle`) must survive.
  const [conflictPending, setConflictPending] = useState(false)

  /**
   * MOVING a pasted image, which is the one case that needs the server to do
   * the whole thing in one request.
   *
   * The bytes live in the message part (no `handle`), and `messagePartId`
   * means there is a chip in view to re-point -- a MOVE. Materialising those
   * bytes client-side first, as the branch below does, charges them again
   * while the inline row is still live, so a member at their limit is refused
   * the one button that would get them under it.
   *
   * A COPY (an inline attachment with no chip in view, so no
   * `messagePartId`) deliberately keeps the old path: it really is new bytes,
   * and charging for them is correct.
   */
  const inlineMove = !handle && !!url && !!messagePartId

  /**
   * Put an inline attachment's bytes into the session CAS store and hand back
   * its handle — the step every inline save used to begin with.
   *
   * Still reached three ways: a COPY, a Replace (below), and an inline
   * attachment with no ledger row to credit (below). Cached in state so a
   * conflict re-call does not upload the same bytes twice.
   */
  // #1316: the inline upload is one of the save's steps — sent as the identity that started it (`batch`), and its
  // handle cached only while that session is still on.
  const materialiseInline = async (batch: PinnedBatch): Promise<string> => {
    if (!url) throw { code: 'ATTACHMENT_NOT_FOUND' }
    // Saved through the SAME endpoint as a handle-backed attachment, so the
    // whole gate stack (destination permission, .pdapp Modification
    // Safeguard, write-freeze / identity-only, name-collision suffixing)
    // applies identically. The staged CAS row is never referenced by a
    // message part, so the reaper reclaims it after its grace window.
    const blob = await (await fetch(url)).blob()
    const file = new File([blob], filename, { type: mime || blob.type || 'application/octet-stream' })
    const uploaded = await uploadSessionAttachment(sessionId, workspaceId, file, {
      idempotencyKey: await saveToDriveIdempotencyKey(filename, blob),
      source: 'upload',
      ...batch.opts(),
    })
    // the handle is the starter's session upload — never cached for a save the next session starts
    if (!batch.ended()) setMaterializedHandle(uploaded.handle)
    return uploaded.handle
  }

  const doSave = async (
    batch: PinnedBatch,
    effectiveHandle: string | undefined,
    opts: { conflictMode?: 'ask' | 'suffix'; resolution?: 'keepBoth' | 'replace' },
    // WHICH ROUTE, decided by the caller rather than read from `inlineMove`
    // here: one `handleSave` can start on the credited move and fall back to
    // the handle endpoint mid-flight, twice over (see below).
    viaInlineMove: boolean,
  ) => {
    if (viaInlineMove) {
      // `filename` goes too: a just-sent chip's part id matches no persisted
      // part, and (message, filename) is what the server falls back to.
      const moved = await requestMoveInlineAttachmentToDrive(sessionId, workspaceId, messagePartId!, {
        destParentId, messageId, filename, ...opts,
      }, batch.opts())
      // #1316: a move that finished after its session ended is shown nothing in the next one
      if (batch.ended()) { setSaving(false); return }
      onSaved({ fileName: moved.fileName, path: moved.path, fileId: moved.fileId })
      return
    }
    if (!effectiveHandle) throw { code: 'ATTACHMENT_NOT_FOUND' }
    const result = await requestSaveSessionAttachmentToDrive(sessionId, workspaceId, effectiveHandle, {
      destParentId, messageId, messagePartId, ...opts,
    }, batch.opts())
    // #1316: a save that finished after its session ended is shown nothing in the next one
    if (batch.ended()) { setSaving(false); return }
    onSaved({ fileName: result.fileName, path: result.path, fileId: result.fileId })
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    // #1316 (codex review): the save is a person-started write — every step (the inline upload, the save, a conflict
    // re-call) is sent as the identity that STARTED it and finishes (`pinnedBatch`), never as an identity switched to
    // meanwhile; what it shows (onSaved, an error, the conflict modal) follows the session. A conflict choice made after
    // that session ended is not sent.
    const batch = pinnedBatch()
    const stopped = (err?: unknown) => batch.ended() || isSessionEndedError(err) || isPinnedBatchIdentityLost(err)
    // Hoisted so the conflict-resolution re-call below reuses the EXACT handle
    // the 'ask' probe used — reading `materializedHandle` state there would be
    // stale (React hasn't re-rendered within this one call).
    let effectiveHandle = handle ?? materializedHandle
    // Starts as the dispatch and can be turned OFF mid-call; the conflict
    // re-call below has to know which route the probe actually took.
    let viaInlineMove = inlineMove
    try {
      // The credited move needs NO handle: the server reads the bytes out of
      // the message part. Only a COPY materialises them up front.
      if (!effectiveHandle && !viaInlineMove) effectiveHandle = await materialiseInline(batch)
      // Always ask first — the server tells us whether this collides before
      // anything is written. A no-collision case just proceeds (fast path,
      // one round-trip); a real collision comes back as a thrown
      // NAME_CONFLICT, which opens the drive's shared conflict modal below.
      try {
        await doSave(batch, effectiveHandle, { conflictMode: 'ask' }, viaInlineMove)
      } catch (probeErr: unknown) {
        // NO LEDGER ROW TO CREDIT — fall back to what this dialog did before
        // the credited route existed, rather than telling the member a file
        // they can see does not exist.
        //
        // Two states reach this, both permanent: a row written with a NULL
        // uploader stays NULL for good (backfill-inline-attachments.ts), and
        // the ledger INSERT on send is deliberately non-fatal
        // (inline-attachment-metering.ts), so a live part can have no row at
        // all. Neither can be credited — there is nothing to credit against —
        // so these bytes get charged, exactly as they used to be.
        if (viaInlineMove && (probeErr as { code?: string }).code === 'INLINE_LEDGER_ROW_MISSING') {
          viaInlineMove = false
          effectiveHandle = await materialiseInline(batch)
          await doSave(batch, effectiveHandle, { conflictMode: 'ask' }, false)
        } else {
          throw probeErr
        }
      }
    } catch (err: unknown) {
      // #1316: a failure after the session ended (or an answer dropped by it) is not a failure to show, and a conflict
      // then opens no modal in the next session
      if (stopped(err)) { setSaving(false); return }
      const e = err as { message?: string; code?: string }
      if (e.code === 'NAME_CONFLICT') {
        // Hand off to the drive's shared ConflictModal (same one copy/move
        // use). A user-chosen Replace (a new version of the existing file) is a
        // DELIBERATE overwrite — never silent — and still passes the full server gate stack.
        // `conflictPending` hides THIS dialog's own modal for the span of the
        // await so the ConflictModal isn't stacked underneath it (see the
        // state declaration above) — always cleared, success or throw.
        let resolution: 'keepBoth' | 'replace' | 'skip'
        setConflictPending(true)
        try {
          ;({ resolution } = await resolveConflict({
            fileName: filename,
            sourcePath: filename,
            destPath: destDisplay,
            isFolder: false,
            operation: 'move',
            showApplyToAll: false,
          }))
        } finally {
          setConflictPending(false)
        }
        if (resolution === 'skip' || batch.ended()) {
          // The human declined — no move. Stay in the dialog so they can pick
          // another folder or cancel outright. (Or the session ended meanwhile — #1316.)
          setSaving(false)
          return
        }
        // A REPLACE GOES THROUGH THE HANDLE ENDPOINT, never the credited
        // move, and this is a correctness rule rather than a preference.
        //
        // Replace writes a new VERSION of the existing drive file. When that
        // file is an app's live database the write must go through the
        // engine-db ADOPT ENTRY (#955 §7.6.5) — validate, close every open
        // handle, atomic swap, under locks taken with no unit of work held.
        // `asReplaceEntryById` performs that only OUTSIDE a scoped
        // transaction, and the chat-storage move route deliberately runs
        // inside the request's transaction (its rollback design depends on
        // it), so a Replace there would skip the adopt and write raw bytes
        // over a live database. The handle endpoint is exempted from the
        // request scope for exactly this reason, so Replace is sent there,
        // and the server refuses one on the move route regardless.
        //
        // The cost, stated plainly: a Replace of a pasted image is still
        // charged twice for the span of the move, as every inline save was
        // before this change. Keep both — the other resolution — keeps the
        // credit.
        // ⚠️ THE MATERIALISE BELONGS INSIDE THIS try, not above it.
        //
        // It is the UNCREDITED charged upload, so for the member this whole
        // branch exists to help — the one at their limit — it is the LIKELY
        // outcome that it is refused (413). Outside a try its rejection
        // escapes `handleSave` altogether: `setSaving(false)` never runs, and
        // with `saving` stuck true the Move button spins, Cancel and Choose
        // folder are disabled and `dismissible={!saving}` stops Escape and
        // the backdrop closing it. The member is left with a locked dialog,
        // no message, and a page reload as the only way out. Review round 2
        // found it; the Replace test stubbed the upload as always resolving,
        // which is why round 1's tests did not.
        try {
          if (resolution === 'replace' && viaInlineMove) {
            viaInlineMove = false
            effectiveHandle = await materialiseInline(batch)
          }
          if (!effectiveHandle && !viaInlineMove) {
            // Defensive — a conflict is only reachable after a handle exists
            // (materialized or provided), or on the handle-less inline move;
            // never silently swallow a bad state.
            setError('Could not move to drive.')
            setSaving(false)
            return
          }
          await doSave(batch, effectiveHandle, { resolution }, viaInlineMove)
        } catch (retryErr: unknown) {
          if (!stopped(retryErr)) setError(describeSaveError(retryErr as { message?: string; code?: string }))
          setSaving(false)
        }
        return
      }
      setError(describeSaveError(e))
      setSaving(false)
    }
  }

  // While the ConflictModal is up, render NOTHING for this dialog's own
  // modal UI (and its DestinationPicker) — see `conflictPending` above. The
  // component stays mounted so `handleSave` keeps running; only the visible
  // overlay is suppressed, so the two full-screen modals are never stacked
  // at once.
  if (conflictPending) return null

  return (
    <>
      <Modal
        title="Move to drive"
        onClose={onClose}
        dismissible={!saving}
        maxWidth="max-w-md"
        footer={
          <>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3 py-1.5 text-sm themed-text-secondary hover:themed-text rounded transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !tenantId}
              className="px-3 py-1.5 text-sm themed-btn-primary rounded transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {saving && <Loader2 size={12} className="animate-spin" />}
              Move
            </button>
          </>
        }
      >
        <div className="px-5 py-4 space-y-3">
          <div className="text-sm themed-text truncate" title={filename}>{filename}</div>

          <div className="flex items-center justify-between gap-2 rounded themed-border border px-3 py-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <Folder size={14} className="themed-text-muted shrink-0" />
              <span className="text-xs themed-text truncate">{destDisplay}</span>
            </div>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              disabled={saving || !tenantId}
              className="text-xs themed-text-secondary hover:themed-text shrink-0 disabled:opacity-50"
            >
              Choose folder…
            </button>
          </div>

          {/* invariant #2 — the privacy-downgrade notice, shown every time,
              never gated behind "don't show again". Surfacing the cost is
              the point (design doc §10.2/§2). */}
          <div
            role="note"
            className="flex items-start gap-2 rounded themed-border border border-amber-500/40 bg-amber-500/10 px-3 py-2"
          >
            <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
            <p className="text-xs themed-text">
              This moves the file into the drive, where everyone with project access can see and edit it. It leaves the chat.
            </p>
          </div>

          {/* #662 — a real same-name collision at the destination is resolved
              through the drive's shared ConflictModal (Replace / Keep both /
              Skip), opened from `handleSave` on a NAME_CONFLICT.
              A user-chosen overwrite is deliberate, never silent (#639). */}
          {error && (
            <div role="alert" className="text-xs text-red-400">{error}</div>
          )}
        </div>
      </Modal>

      {pickerOpen && tenantId && (
        <DestinationPicker
          tenantId={tenantId}
          onSelect={(folderId, path) => {
            setDestParentId(folderId)
            setDestDisplay(path)
            setPickerOpen(false)
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </>
  )
}
